import { Injectable, signal } from '@angular/core';
import { HdChartMatchListState } from '../../library/hd-chart-match-list.component';
import { HdLocksKeysSearchKind, HdLocksKeysSearchMatch } from './hd-locks-keys-chart-search';

/** Zoekstaat van de L&K-zoektocht voor één chart. */
export interface HdLocksKeysChartSearchEntry {
  readonly open: boolean;
  readonly kind: HdLocksKeysSearchKind;
  /**
   * Laatste zoektocht van deze chart met de parameters waarvoor ze geldt
   * (zoektocht, periode, poorten, scope). Wijkt de huidige signatuur af, dan is
   * het resultaat verouderd; `loading` = de zoektocht loopt nog.
   */
  readonly result: {
    readonly signature: string;
    readonly state: HdChartMatchListState<HdLocksKeysSearchMatch>;
  } | null;
}

export const HD_LOCKS_KEYS_CHART_SEARCH_DEFAULT_ENTRY: HdLocksKeysChartSearchEntry = {
  open: false,
  kind: 'both',
  result: null,
};

/** Zoveel charts houden hun zoekstaat bij; de langst niet gebruikte valt eerst weg. */
export const HD_LOCKS_KEYS_CHART_SEARCH_MAX_CHARTS = 20;

/**
 * Zoekstaat van de L&K-zoektocht per chart (sleutel = `activeChartKey` van de
 * zoek-context). Een chartwissel toont zo de staat van die chart: dicht voor
 * een chart waar nog niet gezocht werd, open met de bewaarde treffers voor een
 * chart waar de sectie open bleef, zonder nieuwe zoektocht. Gedeeld door het
 * L&K-paneel en de L&K-sectie van Geavanceerd, zodat die dezelfde staat tonen
 * en één zoektocht delen.
 */
@Injectable({ providedIn: 'root' })
export class HdLocksKeysChartSearchStore {
  private readonly entries = signal<ReadonlyMap<string, HdLocksKeysChartSearchEntry>>(new Map());

  entry(chartKey: string | null): HdLocksKeysChartSearchEntry {
    if (chartKey == null) return HD_LOCKS_KEYS_CHART_SEARCH_DEFAULT_ENTRY;
    return this.entries().get(chartKey) ?? HD_LOCKS_KEYS_CHART_SEARCH_DEFAULT_ENTRY;
  }

  update(chartKey: string | null, patch: Partial<HdLocksKeysChartSearchEntry>): void {
    if (chartKey == null) return;
    this.entries.update(entries => {
      const next = new Map(entries);
      const current = next.get(chartKey) ?? HD_LOCKS_KEYS_CHART_SEARCH_DEFAULT_ENTRY;
      // Opnieuw invoegen = meest recent gebruikt; de oudste valt weg boven het plafond.
      next.delete(chartKey);
      next.set(chartKey, { ...current, ...patch });
      while (next.size > HD_LOCKS_KEYS_CHART_SEARCH_MAX_CHARTS) {
        next.delete(next.keys().next().value!);
      }
      return next;
    });
  }
}
